import apiClient from "./client";

/**
 * @typedef {import('@/types/models/tenant').Tenant} Tenant
 * @typedef {import('@/types/api/responses').PaginatedResponse} PaginatedResponse
 */

/**
 * Tenants API module
 * Returns raw JSON:API responses - normalization happens in Redux thunks
 */
const tenantsAPI = {
  /**
   * Get all tenants accessible to current user
   * @returns {Promise<Object>} JSON:API response with data, meta, links
   */
  getAll: async () => {
    const response = await apiClient.get("/tenants/");
    return response.data;
  },

  /**
   * Get a specific tenant by ID
   * @param {string} tenantId - Tenant UUID
   * @returns {Promise<Object>} JSON:API response with tenant data
   */
  getById: async (tenantId) => {
    const response = await apiClient.get(`/tenants/${tenantId}/`);
    return response.data;
  },

  /**
   * Create a new tenant
   * @param {Object} data - Tenant data
   * @param {string} data.name - Organization name
   * @param {string} [data.slug] - URL-safe identifier
   * @param {string} [data.domain] - Custom domain
   * @returns {Promise<Object>} JSON:API response with created tenant
   */
  create: async (data) => {
    const response = await apiClient.post("/tenants/", data);
    return response.data;
  },

  /**
   * Update a tenant
   * @param {string} tenantId - Tenant UUID
   * @param {Object} data - Updated tenant data
   * @returns {Promise<Object>} JSON:API response with updated tenant
   */
  update: async (tenantId, data) => {
    const response = await apiClient.patch(`/tenants/${tenantId}/`, data);
    return response.data;
  },

  /**
   * Deactivate or permanently delete an organization (owner-only).
   * @param {string} tenantId - Tenant id
   * @param {Object} [options]
   * @param {'deactivate'|'delete'} [options.mode='deactivate'] - 'deactivate' is
   *   reversible; 'delete' schedules an irreversible schema drop after a grace
   *   period and requires confirmName to match the organization name exactly.
   * @param {string} [options.confirmName] - Required when mode is 'delete'.
   * @returns {Promise<Object>} { data: { mode, tenant_id, purge_at } }
   */
  deleteOrganization: async (tenantId, { mode = 'deactivate', confirmName } = {}) => {
    const { data } = await apiClient.delete(`/tenants/${tenantId}/`, {
      data: { mode, confirm_name: confirmName },
    });
    return data;
  },

  /**
   * List organizations the current user owns that are deactivated
   * (self-service reactivate discovery, #187 follow-up).
   * @returns {Promise<Object>} { data: [{ id, name, subdomain, pending_deletion_at }] }
   */
  getDeactivated: async () => {
    const response = await apiClient.get('/tenants/deactivated/');
    return response.data;
  },

  /**
   * Reactivate a deactivated organization (owner-only).
   * @param {string} tenantId - Tenant id
   * @returns {Promise<Object>} { data: { tenant_id, name } }
   */
  reactivate: async (tenantId) => {
    const { data } = await apiClient.post(`/tenants/${tenantId}/reactivate/`);
    return data;
  },

  /**
   * Get tenant members
   * @param {string} tenantId - Tenant UUID
   * @returns {Promise<Object>} JSON:API response with members data
   */
  getMembers: async (tenantId) => {
    const response = await apiClient.get(`/tenants/${tenantId}/members/`);
    return response.data;
  },

  /**
   * Add member to tenant
   * @param {string} tenantId - Tenant UUID
   * @param {Object} data - Member data
   * @param {string} data.email - User email
   * @param {string} data.role - User role
   * @returns {Promise<Object>} JSON:API response with member data
   */
  addMember: async (tenantId, data) => {
    const response = await apiClient.post(`/tenants/${tenantId}/members/`, data);
    return response.data;
  },
};

export default tenantsAPI;
